import {
  Box,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import WorkIcon from '@material-ui/icons/Work';
import { VacancyCandidatesTarget } from 'lib/Model/SimilarCandidates';
import * as React from 'react';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2.5),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: '#FFFFFF',
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  linkButton: {
    padding: 2,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
  },
  linkIcon: {
    fontSize: 15,
  },
  title: {
    fontWeight: 700,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  chip: {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  mustHaves: {
    marginTop: theme.spacing(1.5),
  },
  mustHavesLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  mustHavesList: {
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
}));

function getMustHaveLines(vacancy: VacancyCandidatesTarget): string[] {
  const lines: string[] = [];
  const mustHaves = vacancy.must_haves || {};
  if (mustHaves.languages && mustHaves.languages.length > 0) {
    lines.push(`Languages: ${mustHaves.languages.join(', ')}`);
  }
  if (mustHaves.min_experience_years) {
    lines.push(
      `At least ${mustHaves.min_experience_years} years of experience`,
    );
  }
  for (const other of mustHaves.other || []) {
    lines.push(other);
  }
  return lines;
}

interface Props {
  vacancy: VacancyCandidatesTarget;
}

export const TargetVacancyCard: React.FC<Props> = ({ vacancy }) => {
  const classes = useStyles();
  const mustHaveLines = getMustHaveLines(vacancy);

  const chips: string[] = [];
  if (vacancy.primary_job_focus) chips.push(vacancy.primary_job_focus);
  for (const spjf of vacancy.subprimary_job_focuses || []) chips.push(spjf);
  for (const industry of vacancy.industries || []) chips.push(industry);
  if (vacancy.employment_level) chips.push(vacancy.employment_level);

  return (
    <Paper elevation={0} variant="outlined" className={classes.card}>
      <Box className={classes.iconWrap}>
        <WorkIcon />
      </Box>
      <Box className={classes.content}>
        <Typography className={classes.eyebrow}>
          Target vacancy{' '}
          <Tooltip title="Open vacancy in ATS">
            <IconButton
              component="a"
              href={`https://konnekt.talexiohr.com/ats/vacancies/${vacancy.vacancy_id}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              className={classes.linkButton}
              aria-label={`Open vacancy ${vacancy.vacancy_id} in ATS`}
            >
              <OpenInNewIcon className={classes.linkIcon} />
            </IconButton>
          </Tooltip>
        </Typography>
        <Typography variant="h6" className={classes.title}>
          {vacancy.title || `Vacancy ${vacancy.vacancy_id}`}
        </Typography>
        <Box className={classes.chips}>
          {chips.map((label) => (
            <Chip
              key={label}
              className={classes.chip}
              label={label}
              size="small"
            />
          ))}
          {vacancy.exclude_non_eu && (
            <Chip
              className={classes.chip}
              label="EU candidates only"
              size="small"
              color="primary"
            />
          )}
        </Box>

        {mustHaveLines.length > 0 && (
          <Box className={classes.mustHaves}>
            <Typography className={classes.mustHavesLabel}>
              Hard Requirements Checked
            </Typography>
            <ul className={classes.mustHavesList}>
              {mustHaveLines.map((line) => (
                <Typography key={line} component="li" variant="body2">
                  {line}
                </Typography>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
